import { z } from 'zod';
import type { ChatMessage, CitationTarget, CodexItem, CodexNotification, PdfExtractPreview, PdfExtractPreviewEvent, PdfExtractReferenceRequest } from '@reados/contracts';

const identity = z.string().min(1).max(200);
const workspacePath = z.string().min(1).max(8192).refine(value => value.startsWith('/workspace/')
  && !/[\u0000-\u001f\u007f\\]/.test(value) && value.slice(1).split('/').every(part => part && part !== '.' && part !== '..'));
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const previewSchema = z.object({
  manifestPath: workspacePath, manifestSha256: sha256,
  source: z.object({ fileId: identity, name: z.string().min(1).max(1024), path: workspacePath, contentRevision: sha256, sha256 }).strict()
    .refine(source => source.contentRevision === source.sha256),
  pageLabelKind: z.enum(['book', 'physical']),
  pages: z.array(z.object({ physicalPage: z.number().int().positive().safe(), pageLabel: z.string().min(1).max(1024) }).strict()).min(1).max(80)
    .refine(pages => pages.every((page, index) => !index || page.physicalPage > pages[index - 1].physicalPage)),
  extractedPdfPath: workspacePath, sourceSnapshotPath: workspacePath,
  ocrOnly: z.boolean().optional(),
}).strict();
const paramsSchema = z.object({ ownerThreadId: identity, threadId: identity, turnId: identity, itemId: identity, preview: previewSchema, preserveCommand: z.boolean().optional() });
export const pdfExtractKey = (threadId: string, turnId: string, itemId: string) => JSON.stringify([threadId, turnId, itemId]);
const successful = (item: CodexItem) => item.type === 'commandExecution' && item.exitCode === 0
  && ['completed', 'complete', 'success'].includes(String(item.status));

/** Only the authenticated, server-verified sideband supplies PDF identities.
 * Never infer a document, page, or successful read from shell text or stdout. */
export function pdfExtractPreviews(events: CodexNotification[]): Map<string, PdfExtractPreviewEvent['params']> {
  const previews = new Map<string, PdfExtractPreviewEvent['params']>(), conflicts = new Set<string>();
  for (const event of events) {
    if (event.method !== 'reados/pdfExtract') continue;
    const result = paramsSchema.safeParse(event.params); if (!result.success) continue;
    const { threadId, turnId, itemId } = result.data, key = pdfExtractKey(threadId, turnId, itemId);
    if (conflicts.has(key)) continue;
    if (previews.has(key) && JSON.stringify(previews.get(key)) !== JSON.stringify(result.data)) {
      previews.delete(key); conflicts.add(key); continue;
    }
    previews.set(key, result.data);
  }
  return previews;
}

/** Compress only consecutive decimal labels with consecutive verified pages.
 * Roman/custom labels remain exact rather than guessing a numbering scheme. */
function ranges(preview: PdfExtractPreview): string[] {
  const labels = preview.pages.map(page => preview.pageLabelKind === 'physical' ? String(page.physicalPage) : page.pageLabel);
  const result: string[] = [];
  for (let start = 0; start < labels.length;) {
    let end = start;
    if (/^(0|[1-9]\d*)$/.test(labels[start])) while (end + 1 < labels.length
      && /^(0|[1-9]\d*)$/.test(labels[end + 1]) && Number(labels[end + 1]) === Number(labels[end]) + 1
      && preview.pages[end + 1].physicalPage === preview.pages[end].physicalPage + 1) end++;
    result.push(end > start ? `${labels[start]}–${labels[end]}` : labels[start]); start = end + 1;
  }
  return result;
}

export function pdfExtractPreviewItem(item: CodexItem, key: string, preview: PdfExtractPreview, preserveCommand: unknown = true): Record<string, unknown> | null {
  // iPad's independent --ocr result uses the existing OCR command card. It
  // must not become a colored "viewed PDF" or generic image attachment.
  if (!successful(item) || preview.ocrOnly === true) return null;
  const extractedRanges = ranges(preview), physical = preview.pageLabelKind === 'physical';
  // The API marks only an unambiguous standalone extract as replaceable. Keep
  // every other native activity and its authoritative read/search/list actions.
  const preserve = preserveCommand !== false || (Array.isArray(item.commandActions) && item.commandActions.some(action =>
    action && typeof action === 'object' && ['read', 'search', 'listFiles', 'list_files'].includes(String(action.type))));
  return {
    id: `${item.id}:pdf:${preview.manifestSha256}`, kind: 'file', title: `${physical ? '物理页' : '书页'} ${extractedRanges.join('、')}`,
    subtitle: `《${preview.source.name}》`, documentName: preview.source.name,
    filePath: preview.extractedPdfPath, fileName: preview.extractedPdfPath.split('/').at(-1), mimeType: 'application/pdf', attachmentKind: 'extractedPDF',
    payload: {
      preview_kind: 'pdf_extract', reados_pdf_extract: true, reados_preserve_command: preserve, sourceName: preview.source.name,
      pdf_extract_variant: 'source', extractedPageCount: preview.pages.length, extractedRanges,
      pageNumbers: preview.pages.map(page => page.physicalPage), page_selector_kind: physical ? 'physical_pdf_page' : 'book_page',
      readexContentReference: { contentID: preview.source.fileId, path: preview.source.path,
        pageNumber: preview.pages[0].physicalPage, readosPdfExtractKey: key },
      readex_conversation_artifact: { readosPdfExtractKey: key },
      readosPdfExtractKey: key,
    },
  };
}

/** The iframe requests only an evidence key. Source paths and page identities
 * come from this sideband and are checked again by the API at click time. */
export function pdfExtractNavigationRequest(key: unknown, kind: unknown, messages: ChatMessage[], events: CodexNotification[]): { url: string; body: PdfExtractReferenceRequest } | null {
  if (typeof key !== 'string' || key.length > 1024) return null;
  if (kind !== undefined && kind !== 'source' && kind !== 'extracted') return null;
  const evidence = pdfExtractPreviews(events).get(key); if (!evidence) return null;
  let matched: CodexItem | undefined;
  for (const message of messages) for (const item of message.items || []) {
    if (pdfExtractKey(String(message.threadId || ''), String(message.turnId || ''), item.id) === key) matched = item;
  }
  for (const event of events) {
    const item = event.params?.item;
    if (item && typeof item === 'object' && pdfExtractKey(String(event.params.threadId || ''), String(event.params.turnId || ''), String((item as CodexItem).id || '')) === key) matched = item as CodexItem;
  }
  if (!matched || !successful(matched)) return null;
  return { url: `/api/threads/${encodeURIComponent(evidence.ownerThreadId)}/pdf-extract-reference`,
    body: { threadId: evidence.threadId, turnId: evidence.turnId, itemId: evidence.itemId,
      manifestSha256: evidence.preview.manifestSha256, kind: kind === 'extracted' ? 'extracted' : 'source',
      ...(kind === 'extracted' ? {} : { physicalPage: evidence.preview.pages[0].physicalPage }) } };
}

const targetSchema = z.object({ path: workspacePath, fileId: identity.optional(), pageNumber: z.number().int().positive().safe() }).strict();
export function pdfExtractNavigationTarget(value: unknown): CitationTarget | null {
  const parsed = targetSchema.safeParse(value); return parsed.success ? parsed.data : null;
}
